import { useCallback, useEffect, useRef, useState } from 'react'
import { getToken } from '../clients/config'
import { BASE_API_WSS_GATEWAY_URL } from '../endpoints.constants'

export const useSocket = <T extends object>(url: string, onMessage: (data: T) => void) => {
    const [isConnected, setIsConnected] = useState(false)
    const socket = useRef<WebSocket | null>(null)
    const shouldReconnect = useRef(true)

    const connect = useCallback(() => {
        const currentStatus = socket.current?.readyState

        if (currentStatus === WebSocket.CONNECTING || currentStatus === WebSocket.OPEN) {
            return
        }

        socket.current = new WebSocket(
            `${BASE_API_WSS_GATEWAY_URL}/api/v1/${url}?access_token=${getToken()}`,
        )

        socket.current.onopen = () => {
            setIsConnected(true)
        }

        socket.current.onclose = (event) => {
            setIsConnected(false)

            // 1000 meaning "Normal Closure" https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
            if (event.code !== 1000 && shouldReconnect.current) {
                setTimeout(() => {
                    if (shouldReconnect.current) {
                        connect()
                    }
                }, 5000)
            }
        }

        socket.current.onmessage = (event) => {
            if (!socket.current) {
                return
            }

            try {
                const message = JSON.parse(event.data)

                if (message.type === 'message') {
                    onMessage(message.data)
                }

                if (message.type === 'ping') {
                    socket.current.send(JSON.stringify({ type: 'pong' }))
                }
            } catch (error) {
                console.error('Error parsing message:', error)
            }
        }

        socket.current.onerror = (error) => {
            console.error('WebSocket error:', error)
        }
    }, [url, onMessage])

    const disconnect = useCallback(() => {
        shouldReconnect.current = false
        if (socket.current) {
            socket.current.close(1000)
            socket.current = null
        }
    }, [])

    const sendMessage = useCallback((message) => {
        if (socket.current && socket.current.readyState === WebSocket.OPEN) {
            socket.current.send(JSON.stringify(message))
        } else {
            console.warn('WebSocket not connected. Cannot send message.')
        }
    }, [])

    useEffect(() => {
        shouldReconnect.current = true
        connect()

        return () => {
            shouldReconnect.current = false
            if (socket.current) {
                socket.current.close(1000)
                socket.current = null
            }
        }
    }, [connect])

    return {
        isConnected,
        sendMessage,
        connect,
        disconnect,
    }
}
